import React from "react";
import {
  Autocomplete,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CaretDown } from "@phosphor-icons/react";

interface Ioptions {
  name: string;
  type?: string;
  coverage?: string;
  id: number | string;
}

export default function PayorSelection({
  options,
  label,
  onChange,
  value,
}: {
  options: Ioptions[];
  label: string;
  onChange: (e: { target: { value: string | number } }) => void;
  value: string | number;
}) {
  const theme = useTheme();
  const [inputValue, setInputValue] = React.useState("");
  const [fieldValue, setFieldValue] = React.useState<Ioptions | null>(null);

  React.useEffect(() => {
    const option = options?.find((opt) => opt.id === value) || null;
    setFieldValue(option);
  }, []);

  return (
    <Autocomplete
      disablePortal
      options={options}
      value={fieldValue}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      onChange={(_, newValue) => {
        setFieldValue(newValue);
        onChange({ target: { value: newValue?.id ?? "" } });
      }}
      getOptionLabel={(option) => option.name}
      sx={{
        "&& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
          padding: theme.spacing(0, 4),
          "& .MuiAutocomplete-input": { padding: 0 },
        },
      }}
      renderInput={(params) => {
        const { InputProps, ...rest } = params;
        return (
          <TextField
            {...rest}
            label={label}
            InputProps={{
              ...InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <CaretDown />
                </InputAdornment>
              ),
            }}
          />
        );
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Stack py="2px">
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const typeOrCoverage = (option.type || option.coverage)?.toLowerCase();
                if (!typeOrCoverage) return null;
                else if (typeOrCoverage === "medicaid") return "Medicaid";
                else if (typeOrCoverage === "both") return "Commercial and Medicaid";
                return "Commercial";
              })()}
            </Typography>
          </Stack>
        </li>
      )}
    />
  );
}
